export type Lang = "sv" | "en";

/**
 * Type definitions for routes
 */
type RouteTuple = [string, string];
type NestedRoutes = Record<string, RouteTuple>;
type RouteValue = RouteTuple | Record<string, RouteTuple>;
type RedirectValue = Record<string, Record<string, RouteTuple>>;

/**
 * Centralized route configuration mapping Swedish and English paths.
 * Each entry contains a tuple [svPath, enPath].
 */
export const routes = {
  home: ["/", "/en"] as RouteTuple,
  about: ["/om-oss", "/en/about"] as RouteTuple,
  press: ["/om-oss/press", "/en/about/press"] as RouteTuple,
  careers: ["/karriar", "/en/careers"] as RouteTuple,
  products: ["/plattform", "/en/platform"] as RouteTuple,
  business: ["/for-verksamheten", "/en/for-business"] as RouteTuple,
  contact: ["/kontakt", "/en/contact"] as RouteTuple,
  bookDemo: ["/boka-demo", "/en/book-demo"] as RouteTuple,
  freeAnalysis: [
    "/gratis-schemaanalys",
    "/en/free-schedule-analysis",
  ] as RouteTuple,
  calculator: ["/kalkylator", "/en/calculator"] as RouteTuple,
  faq: ["/vanliga-fragor", "/en/faq"] as RouteTuple,
  trust: ["/trygghet", "/en/trust"] as RouteTuple,
  trustDpa: ["/trygghet/pub", "/en/trust/dpa"] as RouteTuple,
  trustAiAct: ["/trygghet/eu-ai-act", "/en/trust/eu-ai-act"] as RouteTuple,
  pricing: ["/prissattning", "/en/pricing"] as RouteTuple,
  vadArCaire: ["/vad-ar-caire", "/en/what-is-caire"] as RouteTuple,
  saFungerarCaire: ["/sa-fungerar-caire", "/en/how-caire-works"] as RouteTuple,
  plattform: ["/plattform", "/en/platform"] as RouteTuple,
  platform: {
    imageLibrary: [
      "/plattform/bildbibliotek",
      "/en/platform/image-library",
    ] as RouteTuple,
    integrations: [
      "/plattform/integrationer",
      "/en/platform/integrations",
    ] as RouteTuple,
  } as Record<string, RouteTuple>,
  features: {
    index: ["/funktioner", "/en/features"] as RouteTuple,
    scheduling: [
      "/produkt/funktioner/ai-schemalaggning",
      "/en/product/features/ai-scheduling",
    ] as RouteTuple,
    aiScheduling: [
      "/produkt/funktioner/ai-schemalaggning",
      "/en/product/features/ai-scheduling",
    ] as RouteTuple,
    integrations: [
      "/produkt/funktioner/integrationer",
      "/en/product/features/integrations",
    ] as RouteTuple,
    analytics: [
      "/produkt/funktioner/analysverktyg",
      "/en/product/features/analytics",
    ] as RouteTuple,
    reports: [
      "/produkt/funktioner/rapporter",
      "/en/product/features/reports",
    ] as RouteTuple,
    handbook: [
      "/produkt/funktioner/personalhandbok",
      "/en/product/features/employee-handbook",
    ] as RouteTuple,
    mobileApps: [
      "/produkt/funktioner/mobilappar",
      "/en/product/features/mobile-apps",
    ] as RouteTuple,
    caireCoreScheduling: [
      "/produkt/funktioner/ai-schemalaggning",
      "/en/product/features/ai-scheduling",
    ] as RouteTuple,
    visitLifecycle: ["/plattform", "/en/platform"] as RouteTuple,
    resourceManagement: [
      "/produkt/funktioner/onboarding",
      "/en/product/features/onboarding",
    ] as RouteTuple,
    onboarding: [
      "/produkt/funktioner/onboarding",
      "/en/product/features/onboarding",
    ] as RouteTuple,
    aiStaffPlanning: [
      "/produkt/funktioner/ai-personalplanering-hemtjanst",
      "/en/product/features/ai-staff-planning-home-care",
    ] as RouteTuple,
    routeOptimizationHomeCare: [
      "/produkt/funktioner/ruttoptimering-hemtjanst",
      "/en/product/features/route-optimization-home-care",
    ] as RouteTuple,
    aiChat: [
      "/produkt/funktioner/ai-chat",
      "/en/product/features/ai-chat",
    ] as RouteTuple,
    myCaire: [
      "/produkt/funktioner/mobilappar",
      "/en/product/features/mobile-apps",
    ] as RouteTuple,
    securityCompliance: [
      "/produkt/funktioner/sakerhet-efterlevnad",
      "/en/product/features/security-compliance",
    ] as RouteTuple,
    // SEO pages 2026-10-11: one topic each (care plans, comparing proposals).
    carePlans: [
      "/produkt/funktioner/genomforandeplan-hemtjanst",
      "/en/product/features/care-plans-home-care",
    ] as RouteTuple,
    compareProposals: [
      "/produkt/funktioner/jamfor-schemaforslag",
      "/en/product/features/compare-schedule-proposals",
    ] as RouteTuple,
  } as Record<string, RouteTuple>,
  product: {
    index: ["/plattform", "/en/platform"] as RouteTuple,
    overview: ["/plattform", "/en/platform"] as RouteTuple,
    features: ["/plattform", "/en/platform"] as RouteTuple,
    mobileAppTour: [
      "/produkt/mobilapp-rundtur",
      "/en/product/mobile-app-tour",
    ] as RouteTuple,
    dashboardTour: [
      "/produkt/dashboard-rundtur",
      "/en/product/dashboard-tour",
    ] as RouteTuple,
    interactiveDemo: [
      "/produkt/interaktiv-demo",
      "/en/product/interactive-demo",
    ] as RouteTuple,
    // Do NOT add /demo/schedule as a product route — nuked. Legacy URLs 301 in
    // server.ts to app.caire.se/welcome. One demo only: DB Demo SA in the app.
    film: ["/produkt/film", "/en/product/film"] as RouteTuple,
  } as Record<string, RouteTuple>,
  solutions: {
    index: ["/losningar", "/en/solutions"] as RouteTuple,
    private: [
      "/losningar/privat-hemtjanst",
      "/en/solutions/private-home-care",
    ] as RouteTuple,
    chain: [
      "/losningar/hemtjanstkedja",
      "/en/solutions/home-care-chain",
    ] as RouteTuple,
    scheduler: [
      "/losningar/samordnare",
      "/en/solutions/scheduler",
    ] as RouteTuple,
    operations: [
      "/losningar/verksamhetschef",
      "/en/solutions/operations-manager",
    ] as RouteTuple,
    municipalities: [
      "/losningar/kommuner",
      "/en/solutions/municipalities",
    ] as RouteTuple,
    caregivers: [
      "/losningar/vardgivare",
      "/en/solutions/caregivers",
    ] as RouteTuple,
    business: ["/for-verksamheten", "/en/for-business"] as RouteTuple,
  } as Record<string, RouteTuple>,
  resourcesCategory: {
    blog: ["/resurser/blogg", "/en/resources/blog"] as RouteTuple,
    index: ["/resurser", "/en/resources"] as RouteTuple,
    guides: ["/resurser/guider", "/en/resources/guides"] as RouteTuple,
    articles: ["/resurser/artiklar", "/en/resources/articles"] as RouteTuple,
    news: ["/resurser/nyheter", "/en/resources/news"] as RouteTuple,
    comparisons: [
      "/resurser/jamforelser",
      "/en/resources/comparisons",
    ] as RouteTuple,
    developers: [
      "/resurser/utvecklare",
      "/en/resources/developers",
    ] as RouteTuple,
  } as Record<string, RouteTuple>,
  // One tuple per line keeps this data file under the 1000-line ceiling.
  // prettier-ignore
  help: {
    index: ["/hjalp", "/en/help"] as RouteTuple,
    metricsAndTerms: ["/hjalp/matvarden-och-begrepp", "/en/help/metrics-and-terms"] as RouteTuple,
    serviceAreaDemandInsetFlex: ["/hjalp/tjansteomrade-efterfragan-och-insatsflex", "/en/help/service-area-demand-and-inset-flex"] as RouteTuple,
    platformBasicsDataOrder: ["/hjalp/sa-fungerar-caire-innan-forsta-schemat", "/en/help/how-caire-works-before-first-schedule"] as RouteTuple,
    firstScheduleImprovementLoop: ["/hjalp/forsta-schemat-till-battre-losning", "/en/help/first-schedule-improvement-loop"] as RouteTuple,
    optimizationReadiness: ["/hjalp/optimeringsberedskap", "/en/help/optimization-readiness"] as RouteTuple,
    clientsContinuity: ["/hjalp/kundkontinuitet", "/en/help/client-continuity"] as RouteTuple,
    employeesEffectiveInputs: ["/hjalp/medarbetardata-for-rutt-och-kostnad", "/en/help/employee-effective-inputs"] as RouteTuple,
    districts: ["/hjalp/distrikt-och-tackning", "/en/help/districts-and-coverage"] as RouteTuple,
    walkingDistricts: ["/hjalp/gangdistrikt", "/en/help/walking-districts"] as RouteTuple,
    visitsTimeWindowsAndChains: ["/hjalp/tidsfonster-och-besokskedjor", "/en/help/visit-time-windows-and-chains"] as RouteTuple,
    scheduleDiagnostics: ["/hjalp/schemadiagnostik", "/en/help/schedule-diagnostics"] as RouteTuple,
    solutions: ["/hjalp/losningar-och-uppfoljning", "/en/help/solutions-and-follow-up"] as RouteTuple,
    journal: ["/hjalp/journal", "/en/help/journal"] as RouteTuple,
    sickCallsAndStaffing: ["/hjalp/sjukanmalan-och-bemanning", "/en/help/sick-calls-and-staffing"] as RouteTuple,
    planReality: ["/hjalp/plan-mot-verklighet", "/en/help/plan-versus-reality"] as RouteTuple,
    publishSchedule: ["/hjalp/publicera-schema", "/en/help/publish-a-schedule"] as RouteTuple,
    careDecisions: ["/hjalp/bistandsbeslut", "/en/help/care-decisions"] as RouteTuple,
    analysReadAndFixDraft: ["/hjalp/analys-las-och-ratta-ett-utkast", "/en/help/analysis-read-and-fix-a-draft"] as RouteTuple,
    createSchedule: ["/hjalp/skapa-schema", "/en/help/create-a-schedule"] as RouteTuple,
    scheduleAiAssistant: ["/hjalp/ai-assistent-schema", "/en/help/schedule-ai-assistant"] as RouteTuple,
    mobileAppTour: ["/hjalp/mobilapp-rundtur", "/en/help/mobile-app-tour"] as RouteTuple,
    securityAndPersonalData: ["/hjalp/sakerhet-och-personuppgifter", "/en/help/security-and-personal-data"] as RouteTuple,
    resourceOrganization: ["/hjalp/data/organisation", "/en/help/data/organization"] as RouteTuple,
    resourceOperationalSettings: ["/hjalp/data/operativa-installningar", "/en/help/data/operational-settings"] as RouteTuple,
    resourceServiceArea: ["/hjalp/data/tjansteomrade", "/en/help/data/service-area"] as RouteTuple,
    resourceDistrict: ["/hjalp/data/distrikt", "/en/help/data/district"] as RouteTuple,
    resourceEmployees: ["/hjalp/data/medarbetare-och-pass", "/en/help/data/employees-and-shifts"] as RouteTuple,
    resourceClients: ["/hjalp/data/kunder", "/en/help/data/clients"] as RouteTuple,
    resourceVisitTemplates: ["/hjalp/data/genomforandeplan-och-besoksmallar", "/en/help/data/care-plan-and-visit-templates"] as RouteTuple,
    resourceSchedules: ["/hjalp/data/schema", "/en/help/data/schedule"] as RouteTuple,
    resourceSolutions: ["/hjalp/data/losningar", "/en/help/data/solutions"] as RouteTuple,
    resourceDiagnostics: ["/hjalp/data/datakontroll-och-diagnostik", "/en/help/data/data-control-and-diagnostics"] as RouteTuple,
    resourceAskCaire: ["/hjalp/ai-assistent-schema", "/en/help/schedule-ai-assistant"] as RouteTuple,
    accountAndOrganisation: ["/hjalp/skapa-konto-och-organisation", "/en/help/create-account-and-organisation"] as RouteTuple,
    gettingStartedChecklist: ["/hjalp/kom-igang-listan", "/en/help/getting-started-checklist"] as RouteTuple,
    unitsLifecycle: ["/hjalp/enheter-skapa-arkivera-sla-ihop", "/en/help/units-create-archive-merge"] as RouteTuple,
    usersAndRoles: ["/hjalp/anvandare-roller-och-behorigheter", "/en/help/users-roles-and-permissions"] as RouteTuple,
    careDecisionPdfImport: ["/hjalp/importera-kommunbeslut-pdf", "/en/help/import-municipal-decisions-pdf"] as RouteTuple,
    clientPauses: ["/hjalp/uppehall-sjukhusvistelse-och-resor", "/en/help/client-pauses-hospital-and-travel"] as RouteTuple,
    templateSlotRecommendation: ["/hjalp/rekommenderad-dag-och-tid", "/en/help/recommended-day-and-time"] as RouteTuple,
    carePlanServicesAndTemplates: ["/hjalp/genomforandeplan-insatser-och-besoksmallar", "/en/help/care-plan-services-and-visit-templates"] as RouteTuple,
    importAlfaEcare: ["/hjalp/importera-fran-verksamhetssystemet", "/en/help/import-from-your-care-system"] as RouteTuple,
    handbookCustomer: ["/hjalp/handbok-for-kunder-och-anhoriga", "/en/help/handbook-for-clients-and-family"] as RouteTuple,
    importCarefoxApi: ["/hjalp/importera-via-api-kopplingen", "/en/help/import-through-api-connection"] as RouteTuple,
    importCarefox: ["/hjalp/importera-fran-planeringssystemet", "/en/help/import-from-your-planning-system"] as RouteTuple,
    shiftTemplatesWeeklyRoster: ["/hjalp/arbetspassmallar-och-veckoschema", "/en/help/shift-templates-and-weekly-roster"] as RouteTuple,
    settingsCascade: ["/hjalp/installningskaskaden", "/en/help/settings-cascade"] as RouteTuple,
    automaticPlanning: ["/hjalp/automatisk-planering-atta-veckor", "/en/help/automatic-eight-week-planning"] as RouteTuple,
    calendarEditLockUnplan: ["/hjalp/kalendern-flytta-lasa-avplanera-avboka", "/en/help/calendar-move-lock-unplan-cancel"] as RouteTuple,
    unassignedFindPlace: ["/hjalp/hitta-plats-for-otilldelade-besok", "/en/help/find-a-place-for-unassigned-visits"] as RouteTuple,
    enPlanSomHallerReport: ["/hjalp/rapporten-en-plan-som-haller", "/en/help/the-en-plan-som-haller-report"] as RouteTuple,
    employeeApp: ["/hjalp/medarbetarappen", "/en/help/the-employee-app"] as RouteTuple,
    clientFamilyApp: ["/hjalp/kund-och-anhorigappen", "/en/help/the-client-and-family-app"] as RouteTuple,
    appInvite: ["/hjalp/bjud-in-till-appen", "/en/help/invite-employees-and-clients-to-the-app"] as RouteTuple,
    operationsWorkspace: ["/hjalp/driftvyn", "/en/help/the-operations-view"] as RouteTuple,
    changedVisitsAndTimeChanges: ["/hjalp/andrade-besok-och-tidsandringar", "/en/help/changed-visits-and-time-changes"] as RouteTuple,
    clientCancellations: ["/hjalp/avbokningar-fran-kund", "/en/help/client-cancellations"] as RouteTuple,
    delayNoticesAndEta: ["/hjalp/forseningsbesked-och-eta", "/en/help/delay-notices-and-eta"] as RouteTuple,
    automaticReplanning: ["/hjalp/automatisk-omplanering-under-dagen", "/en/help/automatic-replanning-during-the-day"] as RouteTuple,
    shiftSwapExtraShiftsAvailability: ["/hjalp/skiftbyte-extrapass-och-tillganglighet", "/en/help/shift-swaps-extra-shifts-and-availability"] as RouteTuple,
    copyDayAndTravelTime: ["/hjalp/kopiera-dag-och-justera-restid", "/en/help/copy-day-and-adjust-travel-time"] as RouteTuple,
    scorecardOptimizationGrade: ["/hjalp/matkortet-och-optimeringsgrad", "/en/help/scorecard-and-optimisation-grade"] as RouteTuple,
    handbookAdminStart: ["/hjalp/personalhandboken-kom-igang", "/en/help/employee-handbook-getting-started"] as RouteTuple,
    handbookTemplates: ["/hjalp/personalhandboken-caire-mallar", "/en/help/employee-handbook-caire-templates"] as RouteTuple,
    handbookCategories: ["/hjalp/personalhandboken-kategorier", "/en/help/employee-handbook-categories"] as RouteTuple,
    handbookOwnPages: ["/hjalp/personalhandboken-egna-sidor", "/en/help/employee-handbook-own-pages"] as RouteTuple,
    handbookContacts: ["/hjalp/personalhandboken-kontakter", "/en/help/employee-handbook-contacts"] as RouteTuple,
    handbookFaqPages: ["/hjalp/personalhandboken-vanliga-fragor", "/en/help/employee-handbook-faq-pages"] as RouteTuple,
    handbookVisibilityAcknowledgement: ["/hjalp/personalhandboken-synlighet-och-laskvittens", "/en/help/employee-handbook-visibility-and-read-receipts"] as RouteTuple,
    handbookOnboardingPackages: ["/hjalp/personalhandboken-introduktionspaket", "/en/help/employee-handbook-onboarding-packages"] as RouteTuple,
    handbookPdfExport: ["/hjalp/personalhandboken-pdf-export", "/en/help/employee-handbook-pdf-export"] as RouteTuple,
    handbookLanguages: ["/hjalp/personalhandboken-sprak-och-oversattningar", "/en/help/employee-handbook-languages-and-translations"] as RouteTuple,
    handbookBrandingMediaShortcuts: ["/hjalp/personalhandboken-varumarke-bilder-och-genvagar", "/en/help/employee-handbook-branding-images-and-shortcuts"] as RouteTuple,
    handbookReading: ["/hjalp/personalhandboken-i-appen-och-pa-webben", "/en/help/employee-handbook-in-the-app-and-on-the-web"] as RouteTuple,
    handbookEmployeeOnboarding: ["/hjalp/personalhandboken-din-introduktion", "/en/help/employee-handbook-your-onboarding"] as RouteTuple,
    handbookContactsFaqReading: ["/hjalp/personalhandboken-kontakter-och-vanliga-fragor", "/en/help/employee-handbook-contacts-and-faq"] as RouteTuple,
    handbookReadConfirmation: ["/hjalp/personalhandboken-bekrafta-att-du-har-last", "/en/help/employee-handbook-confirm-you-have-read"] as RouteTuple,
    stageKomIgang: ["/hjalp/steg/kom-igang", "/en/help/steps/getting-started"] as RouteTuple,
    stageKommunbeslut: ["/hjalp/steg/kommunbeslut", "/en/help/steps/municipal-decisions"] as RouteTuple,
    stageGenomforandeplan: ["/hjalp/steg/genomforandeplan", "/en/help/steps/care-plan"] as RouteTuple,
    stagePersonal: ["/hjalp/steg/personal-och-arbetspass", "/en/help/steps/staff-and-shifts"] as RouteTuple,
    stagePlanering: ["/hjalp/steg/besoks-och-skiftplanering", "/en/help/steps/visit-and-shift-planning"] as RouteTuple,
    stageGranska: ["/hjalp/steg/granska-och-forbattra", "/en/help/steps/review-and-improve"] as RouteTuple,
    stagePublicerat: ["/hjalp/steg/publicerat-schema", "/en/help/steps/published-schedule"] as RouteTuple,
    stageDrift: ["/hjalp/steg/dagen-i-drift", "/en/help/steps/the-working-day"] as RouteTuple,
    stageUppfoljning: ["/hjalp/steg/dokumentation-och-uppfoljning", "/en/help/steps/documentation-and-follow-up"] as RouteTuple,
    collectionInstallningar: ["/hjalp/installningar", "/en/help/settings"] as RouteTuple,
    collectionAi: ["/hjalp/ai", "/en/help/ai"] as RouteTuple,
    collectionPersonalhandboken: ["/hjalp/personalhandboken", "/en/help/employee-handbook"] as RouteTuple,
    collectionIntegrationer: ["/hjalp/integrationer", "/en/help/integrations"] as RouteTuple,
    collectionVanligaFragor: ["/hjalp/vanliga-fragor", "/en/help/faq"] as RouteTuple,
    allArticles: ["/hjalp/artiklar", "/en/help/articles"] as RouteTuple,
    dataIndex: ["/hjalp/data", "/en/help/data"] as RouteTuple,
  } as Record<string, RouteTuple>,
  legal: {
    privacy: ["/integritetspolicy", "/en/privacy"] as RouteTuple,
    terms: ["/anvandarvillkor", "/en/terms"] as RouteTuple,
    unsubscribe: ["/avregistrera", "/en/unsubscribe"] as RouteTuple,
  } as Record<string, RouteTuple>,
  articles: {
    freeScheduleAnalysis: [
      "/resurser/artiklar/gratis-schemaanalys-sa-fungerar-det",
      "/en/resources/articles/free-schedule-analysis-how-it-works",
    ] as RouteTuple,
    aiAssistantMcp: [
      "/resurser/artiklar/ai-assistent-mcp",
      "/en/resources/articles/ai-assistant-mcp",
    ] as RouteTuple,
    saivoInvestment: [
      "/resurser/artiklar/saivo-investerar-i-caire",
      "/en/resources/articles/saivo-invests-in-caire",
    ] as RouteTuple,
    futureHomeCare: [
      "/resurser/artiklar/framtidens-hemtjanst-trender",
      "/en/resources/articles/future-home-care-trends",
    ] as RouteTuple,
    comprehensiveAiInHomeCare: [
      "/resurser/artiklar/omfattande-guide-ai-i-hemtjansten",
      "/en/resources/articles/comprehensive-ai-in-home-care",
    ] as RouteTuple,
    optimizationContinuityAlgorithms: [
      "/resurser/artiklar/optimering-kontinuitet-algoritmer",
      "/en/resources/articles/optimization-continuity-algorithms",
    ] as RouteTuple,
    vrptwHomeCareOptimization: [
      "/resurser/artiklar/vrptw-hemtjanst-optimering",
      "/en/resources/articles/vrptw-home-care-optimization",
    ] as RouteTuple,
    balancingEfficiencyContinuity: [
      "/resurser/artiklar/balansera-effektivitet-kontinuitet",
      "/en/resources/articles/balancing-efficiency-continuity",
    ] as RouteTuple,
    kontinuitetEffektivitetAI: [
      "/resurser/artiklar/kontinuitet-effektivitet-ai",
      "/en/resources/articles/continuity-efficiency-ai",
    ] as RouteTuple,
    constraintManagementHomeCareScheduling: [
      "/resurser/artiklar/villkorshantering-hemtjanst-schemaoptimering",
      "/en/resources/articles/constraint-management-homecare-scheduling",
    ] as RouteTuple,
    efficiency80Hemtjanst: [
      "/resurser/artiklar/80-procent-brukartid-hemtjanst",
      "/en/resources/articles/80-percent-efficiency-home-care",
    ] as RouteTuple,
    routingScienceHomeCare: [
      "/resurser/artiklar/routing-science-hemtjanst",
      "/en/resources/articles/routing-science-home-care",
    ] as RouteTuple,
    scheduleOptimizationCareTimeTravelTime: [
      "/resurser/artiklar/schemaoptimering-brukartid-restid",
      "/en/resources/articles/schedule-optimization-care-time-travel-time",
    ] as RouteTuple,
    missionAfterMs: [
      "/resurser/artiklar/mission-efter-ms",
      "/en/resources/articles/mission-beyond-limits",
    ] as RouteTuple,
    aiSchedulingHomeCare: [
      "/resurser/artiklar/ai-schemalaggning-hemtjanst",
      "/en/resources/articles/ai-scheduling-home-care",
    ] as RouteTuple,
    reduceTravelTimeHomeCare: [
      "/resurser/artiklar/minska-restid-hemtjanst",
      "/en/resources/articles/reduce-travel-time-home-care",
    ] as RouteTuple,
    staffPlanningHomeCare: [
      "/resurser/artiklar/personalplanering-hemtjanst",
      "/en/resources/articles/staff-planning-home-care",
    ] as RouteTuple,
    carefoxAiScheduling: [
      "/resurser/artiklar/carefox-ai-schemalaggning",
      "/en/resources/articles/carefox-ai-scheduling",
    ] as RouteTuple,
    unassignedVisitsHomeCare: [
      "/resurser/artiklar/otilldelade-besok-hemtjanst",
      "/en/resources/articles/unassigned-visits-home-care",
    ] as RouteTuple,
    planningSystemCostHomeCare: [
      "/resurser/artiklar/vad-kostar-planeringssystem-hemtjanst",
      "/en/resources/articles/home-care-planning-system-cost",
    ] as RouteTuple,
    dayInHomeCare2026: [
      "/resurser/artiklar/en-dag-i-hemtjansten-2026",
      "/en/resources/articles/a-day-in-home-care-2026",
    ] as RouteTuple,
    ibicHomeCare: [
      "/resurser/artiklar/ibic-hemtjanst",
      "/en/resources/articles/ibic-home-care",
    ] as RouteTuple,
    employeeHandbookHomeCare: [
      "/resurser/artiklar/personalhandbok-hemtjanst",
      "/en/resources/articles/employee-handbook-home-care",
    ] as RouteTuple,
    socialDocumentationHomeCare: [
      "/resurser/artiklar/social-dokumentation-hemtjanst",
      "/en/resources/articles/social-documentation-home-care",
    ] as RouteTuple,
  } as Record<string, RouteTuple>,
  guides: {
    optimizeSchedule: [
      "/resurser/guider/optimera-hemtjanst-schema",
      "/en/resources/guides/optimize-home-care-schedule",
    ] as RouteTuple,
    effectiveStaffPlanning: [
      "/resurser/guider/effektiv-personalplanering-hemtjanst",
      "/en/resources/guides/effective-staff-planning-home-care",
    ] as RouteTuple,
    comprehensiveAiImplementation: [
      "/resurser/guider/comprehensive-ai-implementation-guide",
      "/en/resources/guides/comprehensive-ai-implementation",
    ] as RouteTuple,
  } as Record<string, RouteTuple>,
  comparisons: {
    routePatternsVsDynamic: [
      "/resurser/jamforelser/slingplanering-vs-dynamisk-besoksplanering",
      "/en/resources/comparisons/route-patterns-vs-dynamic-visit-planning",
    ] as RouteTuple,
    schedulingSystems: [
      "/resurser/jamforelser/jamforelse-schemalaggningssystem-hemtjanst",
      "/en/resources/comparisons/home-care-scheduling-tools-comparison",
    ] as RouteTuple,
    excelVsAI: [
      "/resurser/jamforelser/excel-vs-ai",
      "/en/resources/comparisons/excel-vs-ai",
    ] as RouteTuple,
  } as Record<string, RouteTuple>,
  productServices: {} as Record<string, RouteTuple>,
  investors: {
    aiOsBusiness: ["/investerare/ai-os", "/en/investors/ai-os"] as RouteTuple,
  } as Record<string, RouteTuple>,
  careersAgenticWorkflow: [
    "/karriar/agentiskt-arbetsflode",
    "/en/careers/agentic-workflow",
  ] as RouteTuple,
  // One page per open role: Google only reads JobPosting markup on a page
  // that describes a single job, never on a list of jobs.
  careerRoles: {
    headOfAi: ["/karriar/head-of-ai", "/en/careers/head-of-ai"] as RouteTuple,
    customerSuccessManager: [
      "/karriar/customer-success-manager",
      "/en/careers/customer-success-manager",
    ] as RouteTuple,
  } as Record<string, RouteTuple>,
  redirects: {
    featuresOld: {
      index: ["/funktioner", "/plattform"] as RouteTuple,
      // Repointed to the homepage 2026-09-05 — see the note on this pair in
      // redirectMap.ts. Keep this tuple and that one in step: redirectMap
      // loads this table first and a later entry would silently win, leaving
      // the curated record here saying something the site does not do.
      scheduling: ["/funktioner/schemaläggning", "/"] as RouteTuple,
      integrations: [
        "/funktioner/integrationer",
        "/hjalp/sa-fungerar-caire-innan-forsta-schemat",
      ] as RouteTuple,
      analytics: [
        "/funktioner/analysverktyg",
        "/produkt/funktioner/analysverktyg",
      ] as RouteTuple,
      onboarding: [
        "/funktioner/onboarding",
        "/produkt/funktioner/onboarding",
      ] as RouteTuple,
    },
    servicesOld: {
      index: ["/tjanster", "/plattform"] as RouteTuple,
    },
    retiredProductServices: {
      digitalHandbookSv: [
        "/produkt/tjanster/personalhandbok",
        "/plattform",
      ] as RouteTuple,
      digitalHandbookEn: [
        "/en/product/services/employee-handbook",
        "/en/platform",
      ] as RouteTuple,
      webDevSv: [
        "/produkt/tjanster/webb-utveckling",
        "/plattform",
      ] as RouteTuple,
      webDevEn: ["/en/product/services/web-dev", "/en/platform"] as RouteTuple,
    },
    businessOld: {
      index: ["/for-verksamheten", "/losningar"] as RouteTuple,
    },
    englishRedirects: {
      features: ["/en/features", "/en/platform"] as RouteTuple,
      services: ["/en/services", "/en/platform"] as RouteTuple,
      business: ["/en/for-business", "/en/solutions"] as RouteTuple,
    },
    comparisonRedirects: {
      schedulingSystems: [
        "/resurser/jamforelser/jamforelse-schemalaggningssystem",
        "/resurser/jamforelser/jamforelse-schemalaggningssystem-hemtjanst",
      ] as RouteTuple,
    },
    platformBrand2Migration: {
      indexSv: ["/platform/sv/index.html", "/plattform"] as RouteTuple,
      indexEn: ["/platform/en/index.html", "/en/platform"] as RouteTuple,
      schedulingSv: [
        "/platform/sv/scheduling.html",
        "/produkt/funktioner/ai-schemalaggning",
      ] as RouteTuple,
      schedulingEn: [
        "/platform/en/scheduling.html",
        "/en/product/features/ai-scheduling",
      ] as RouteTuple,
      schedulingArchitectureSv: [
        "/platform/sv/ess-fsr-technical.html",
        "/produkt/funktioner/ai-schemalaggning",
      ] as RouteTuple,
      schedulingArchitectureEn: [
        "/platform/en/ess-fsr-technical.html",
        "/en/product/features/ai-scheduling",
      ] as RouteTuple,
      visitLifecycleSv: [
        "/platform/sv/visit-lifecycle.html",
        "/plattform",
      ] as RouteTuple,
      visitLifecycleEn: [
        "/platform/en/visit-lifecycle.html",
        "/en/platform",
      ] as RouteTuple,
      resourcesSv: [
        "/platform/sv/resources.html",
        "/produkt/funktioner/onboarding",
      ] as RouteTuple,
      resourcesEn: [
        "/platform/en/resources.html",
        "/en/product/features/onboarding",
      ] as RouteTuple,
      analyticsSv: [
        "/platform/sv/analytics.html",
        "/produkt/funktioner/analysverktyg",
      ] as RouteTuple,
      analyticsEn: [
        "/platform/en/analytics.html",
        "/en/product/features/analytics",
      ] as RouteTuple,
      mobileAppsSv: [
        "/platform/sv/mobile-apps.html",
        "/produkt/funktioner/mobilappar",
      ] as RouteTuple,
      mobileAppsEn: [
        "/platform/en/mobile-apps.html",
        "/en/product/features/mobile-apps",
      ] as RouteTuple,
      employeeHandbookSv: [
        "/platform/sv/employee-handbook.html",
        "/produkt/funktioner/mobilappar",
      ] as RouteTuple,
      employeeHandbookEn: [
        "/platform/en/employee-handbook.html",
        "/en/product/features/mobile-apps",
      ] as RouteTuple,
      automaticStaffingSv: [
        "/platform/sv/automatic-staffing.html",
        "/produkt/funktioner/ai-personalplanering-hemtjanst",
      ] as RouteTuple,
      automaticStaffingEn: [
        "/platform/en/automatic-staffing.html",
        "/en/product/features/ai-staff-planning-home-care",
      ] as RouteTuple,
      dataFlowEn: ["/platform/en/data-flow.html", "/en/platform"] as RouteTuple,
      myCaireSv: [
        "/platform/sv/mycaire-ecosystem.html",
        "/produkt/funktioner/mobilappar",
      ] as RouteTuple,
      myCaireEn: [
        "/platform/en/mycaire-ecosystem.html",
        "/en/product/features/mobile-apps",
      ] as RouteTuple,
      platformOverviewSv: [
        "/platform/sv/platform-overview.html",
        "/plattform",
      ] as RouteTuple,
      platformOverviewEn: [
        "/platform/en/platform-overview.html",
        "/en/platform",
      ] as RouteTuple,
      aiComplianceSv: [
        "/platform/sv/ai-compliance.html",
        "/trygghet/eu-ai-act",
      ] as RouteTuple,
      aiComplianceEn: [
        "/platform/en/ai-compliance.html",
        "/en/trust/eu-ai-act",
      ] as RouteTuple,
      routingScienceSv: [
        "/platform/sv/routing-science.html",
        "/resurser/artiklar/routing-science-hemtjanst",
      ] as RouteTuple,
      routingScienceEn: [
        "/platform/en/routing-science.html",
        "/en/resources/articles/routing-science-home-care",
      ] as RouteTuple,
      agenticWorkflowSv: [
        "/platform/sv/agentic-workflow/",
        "/karriar/agentiskt-arbetsflode",
      ] as RouteTuple,
      agenticWorkflowEn: [
        "/platform/en/agentic-workflow/",
        "/en/careers/agentic-workflow",
      ] as RouteTuple,
      agenticWorkflowHtmlSv: [
        "/platform/sv/agentic-workflow.html",
        "/karriar/agentiskt-arbetsflode",
      ] as RouteTuple,
      agenticWorkflowHtmlEn: [
        "/platform/en/agentic-workflow.html",
        "/en/careers/agentic-workflow",
      ] as RouteTuple,
      investorAccessSv: [
        "/platform/sv/investor-access.html",
        "/investerare/ai-os",
      ] as RouteTuple,
      investorAccessEn: [
        "/platform/en/investor-access.html",
        "/en/investors/ai-os",
      ] as RouteTuple,
      investmentDetailsSv: [
        "/platform/sv/investment-details.html",
        "/investerare/ai-os",
      ] as RouteTuple,
      investmentDetailsEn: [
        "/platform/en/investment-details.html",
        "/en/investors/ai-os",
      ] as RouteTuple,
      aiOsBusinessSv: [
        "/platform/sv/ai-os-business.html",
        "/investerare/ai-os",
      ] as RouteTuple,
      aiOsBusinessEn: [
        "/platform/en/ai-os-business.html",
        "/en/investors/ai-os",
      ] as RouteTuple,
    },
    helpLegacy: {
      indexSv: ["/platform/sv/help/", "/hjalp"] as RouteTuple,
      indexEn: ["/platform/en/help/", "/en/help"] as RouteTuple,
      indexHtmlSv: ["/platform/sv/help/index.html", "/hjalp"] as RouteTuple,
      indexHtmlEn: ["/platform/en/help/index.html", "/en/help"] as RouteTuple,
      serviceAreaDemandInsetFlexSv: [
        "/platform/sv/help/service-area-demand-and-inset-flex.html",
        "/hjalp/tjansteomrade-efterfragan-och-insatsflex",
      ] as RouteTuple,
      serviceAreaDemandInsetFlexEn: [
        "/platform/en/help/service-area-demand-and-inset-flex.html",
        "/en/help/service-area-demand-and-inset-flex",
      ] as RouteTuple,
      platformBasicsDataOrderSv: [
        "/platform/sv/help/how-caire-works-before-first-schedule.html",
        "/hjalp/sa-fungerar-caire-innan-forsta-schemat",
      ] as RouteTuple,
      platformBasicsDataOrderEn: [
        "/platform/en/help/how-caire-works-before-first-schedule.html",
        "/en/help/how-caire-works-before-first-schedule",
      ] as RouteTuple,
      optimizationReadinessSv: [
        "/platform/sv/help/optimization-readiness.html",
        "/hjalp/optimeringsberedskap",
      ] as RouteTuple,
      optimizationReadinessEn: [
        "/platform/en/help/optimization-readiness.html",
        "/en/help/optimization-readiness",
      ] as RouteTuple,
      firstScheduleImprovementLoopSv: [
        "/platform/sv/help/first-schedule-improvement-loop.html",
        "/hjalp/forsta-schemat-till-battre-losning",
      ] as RouteTuple,
      firstScheduleImprovementLoopEn: [
        "/platform/en/help/first-schedule-improvement-loop.html",
        "/en/help/first-schedule-improvement-loop",
      ] as RouteTuple,
      clientsContinuitySv: [
        "/platform/sv/help/clients-continuity.html",
        "/hjalp/kundkontinuitet",
      ] as RouteTuple,
      clientsContinuityEn: [
        "/platform/en/help/clients-continuity.html",
        "/en/help/client-continuity",
      ] as RouteTuple,
      employeesEffectiveInputsSv: [
        "/platform/sv/help/employees-effective-inputs.html",
        "/hjalp/medarbetardata-for-rutt-och-kostnad",
      ] as RouteTuple,
      employeesEffectiveInputsEn: [
        "/platform/en/help/employees-effective-inputs.html",
        "/en/help/employee-effective-inputs",
      ] as RouteTuple,
      districtsSv: [
        "/platform/sv/help/districts.html",
        "/hjalp/distrikt-och-tackning",
      ] as RouteTuple,
      districtsEn: [
        "/platform/en/help/districts.html",
        "/en/help/districts-and-coverage",
      ] as RouteTuple,
      walkingDistrictsSv: [
        "/platform/sv/help/walking-districts.html",
        "/hjalp/gangdistrikt",
      ] as RouteTuple,
      walkingDistrictsEn: [
        "/platform/en/help/walking-districts.html",
        "/en/help/walking-districts",
      ] as RouteTuple,
      visitsTimeWindowsAndChainsSv: [
        "/platform/sv/help/visits-time-windows-and-chains.html",
        "/hjalp/tidsfonster-och-besokskedjor",
      ] as RouteTuple,
      visitsTimeWindowsAndChainsEn: [
        "/platform/en/help/visits-time-windows-and-chains.html",
        "/en/help/visit-time-windows-and-chains",
      ] as RouteTuple,
      scheduleDiagnosticsSv: [
        "/platform/sv/help/schedule-diagnostics.html",
        "/hjalp/schemadiagnostik",
      ] as RouteTuple,
      scheduleDiagnosticsEn: [
        "/platform/en/help/schedule-diagnostics.html",
        "/en/help/schedule-diagnostics",
      ] as RouteTuple,
      solutionsSv: [
        "/platform/sv/help/solutions.html",
        "/hjalp/losningar-och-uppfoljning",
      ] as RouteTuple,
      solutionsEn: [
        "/platform/en/help/solutions.html",
        "/en/help/solutions-and-follow-up",
      ] as RouteTuple,
      scheduleAiAssistantSv: [
        "/platform/sv/help/schedule-ai-assistant.html",
        "/hjalp/ai-assistent-schema",
      ] as RouteTuple,
      scheduleAiAssistantEn: [
        "/platform/en/help/schedule-ai-assistant.html",
        "/en/help/schedule-ai-assistant",
      ] as RouteTuple,
      mobileAppTourSv: [
        "/platform/sv/help/mobile-app-tour.html",
        "/hjalp/mobilapp-rundtur",
      ] as RouteTuple,
      mobileAppTourEn: [
        "/platform/en/help/mobile-app-tour.html",
        "/en/help/mobile-app-tour",
      ] as RouteTuple,
      mcpCarePlanningSv: [
        "/platform/sv/help/connect-an-ai-assistant-mcp.html",
        "/resurser/utvecklare#mcp",
      ] as RouteTuple,
      mcpCarePlanningEn: [
        "/platform/en/help/connect-an-ai-assistant-mcp.html",
        "/en/resources/developers#mcp",
      ] as RouteTuple,
      securityAndPersonalDataSv: [
        "/platform/sv/help/security-and-personal-data.html",
        "/hjalp/sakerhet-och-personuppgifter",
      ] as RouteTuple,
      securityAndPersonalDataEn: [
        "/platform/en/help/security-and-personal-data.html",
        "/en/help/security-and-personal-data",
      ] as RouteTuple,
    },
    platformAgenticWorkflowDeepLinks: {
      indexSv: [
        "/platform/sv/agentic-workflow/index.html",
        "/karriar/agentiskt-arbetsflode",
      ] as RouteTuple,
      indexEn: [
        "/platform/en/agentic-workflow/index.html",
        "/en/careers/agentic-workflow",
      ] as RouteTuple,
      visionSv: [
        "/platform/sv/agentic-workflow/vision-and-mandate.html",
        "/karriar/agentiskt-arbetsflode",
      ] as RouteTuple,
      visionEn: [
        "/platform/en/agentic-workflow/vision-and-mandate.html",
        "/en/careers/agentic-workflow",
      ] as RouteTuple,
      rolesSv: [
        "/platform/sv/agentic-workflow/agent-roles-and-model-routing.html",
        "/karriar/agentiskt-arbetsflode",
      ] as RouteTuple,
      rolesEn: [
        "/platform/en/agentic-workflow/agent-roles-and-model-routing.html",
        "/en/careers/agentic-workflow",
      ] as RouteTuple,
      orchestratorSv: [
        "/platform/sv/agentic-workflow/darwin-as-orchestrator.html",
        "/karriar/agentiskt-arbetsflode",
      ] as RouteTuple,
      orchestratorEn: [
        "/platform/en/agentic-workflow/darwin-as-orchestrator.html",
        "/en/careers/agentic-workflow",
      ] as RouteTuple,
      componentMapSv: [
        "/platform/sv/agentic-workflow/darwin-component-map.html",
        "/karriar/agentiskt-arbetsflode",
      ] as RouteTuple,
      componentMapEn: [
        "/platform/en/agentic-workflow/darwin-component-map.html",
        "/en/careers/agentic-workflow",
      ] as RouteTuple,
      modelAgnosticismSv: [
        "/platform/sv/agentic-workflow/model-and-vendor-agnosticism.html",
        "/karriar/agentiskt-arbetsflode",
      ] as RouteTuple,
      modelAgnosticismEn: [
        "/platform/en/agentic-workflow/model-and-vendor-agnosticism.html",
        "/en/careers/agentic-workflow",
      ] as RouteTuple,
      prdPipelineSv: [
        "/platform/sv/agentic-workflow/prd-to-pr-pipeline.html",
        "/karriar/agentiskt-arbetsflode",
      ] as RouteTuple,
      prdPipelineEn: [
        "/platform/en/agentic-workflow/prd-to-pr-pipeline.html",
        "/en/careers/agentic-workflow",
      ] as RouteTuple,
      reviewerLoopSv: [
        "/platform/sv/agentic-workflow/reviewer-feedback-loop.html",
        "/karriar/agentiskt-arbetsflode",
      ] as RouteTuple,
      reviewerLoopEn: [
        "/platform/en/agentic-workflow/reviewer-feedback-loop.html",
        "/en/careers/agentic-workflow",
      ] as RouteTuple,
      scaleOrKillSv: [
        "/platform/sv/agentic-workflow/scale-or-kill.html",
        "/karriar/agentiskt-arbetsflode",
      ] as RouteTuple,
      scaleOrKillEn: [
        "/platform/en/agentic-workflow/scale-or-kill.html",
        "/en/careers/agentic-workflow",
      ] as RouteTuple,
      specAsContractSv: [
        "/platform/sv/agentic-workflow/spec-as-contract.html",
        "/karriar/agentiskt-arbetsflode",
      ] as RouteTuple,
      specAsContractEn: [
        "/platform/en/agentic-workflow/spec-as-contract.html",
        "/en/careers/agentic-workflow",
      ] as RouteTuple,
      throughputSv: [
        "/platform/sv/agentic-workflow/throughput-and-business-signals.html",
        "/karriar/agentiskt-arbetsflode",
      ] as RouteTuple,
      throughputEn: [
        "/platform/en/agentic-workflow/throughput-and-business-signals.html",
        "/en/careers/agentic-workflow",
      ] as RouteTuple,
      verificationSv: [
        "/platform/sv/agentic-workflow/verification-and-evidence.html",
        "/karriar/agentiskt-arbetsflode",
      ] as RouteTuple,
      verificationEn: [
        "/platform/en/agentic-workflow/verification-and-evidence.html",
        "/en/careers/agentic-workflow",
      ] as RouteTuple,
    },
    platformSchedulingPrototypeDeepLinks: {
      mockupsIndexSv: [
        "/platform/sv/mockups/index.html",
        "/produkt/funktioner/ai-schemalaggning",
      ] as RouteTuple,
      mockupsIndexEn: [
        "/platform/en/mockups/index.html",
        "/en/product/features/ai-scheduling",
      ] as RouteTuple,
      mockupsStep1Sv: [
        "/platform/sv/mockups/step-1-select-create.html",
        "/produkt/funktioner/ai-schemalaggning",
      ] as RouteTuple,
      mockupsStep1En: [
        "/platform/en/mockups/step-1-select-create.html",
        "/en/product/features/ai-scheduling",
      ] as RouteTuple,
      mockupsStep2Sv: [
        "/platform/sv/mockups/step-2-optimization.html",
        "/produkt/funktioner/ai-schemalaggning",
      ] as RouteTuple,
      mockupsStep2En: [
        "/platform/en/mockups/step-2-optimization.html",
        "/en/product/features/ai-scheduling",
      ] as RouteTuple,
      mockupsStep3Sv: [
        "/platform/sv/mockups/step-3-customer.html",
        "/produkt/funktioner/ai-schemalaggning",
      ] as RouteTuple,
      mockupsStep3En: [
        "/platform/en/mockups/step-3-customer.html",
        "/en/product/features/ai-scheduling",
      ] as RouteTuple,
      mockupsStep4Sv: [
        "/platform/sv/mockups/step-4-finalize.html",
        "/produkt/funktioner/ai-schemalaggning",
      ] as RouteTuple,
      mockupsStep4En: [
        "/platform/en/mockups/step-4-finalize.html",
        "/en/product/features/ai-scheduling",
      ] as RouteTuple,
      scheduleMockupsIndexSv: [
        "/platform/sv/schedule-mockups/index.html",
        "/produkt/funktioner/ai-schemalaggning",
      ] as RouteTuple,
      scheduleMockupsIndexEn: [
        "/platform/en/schedule-mockups/index.html",
        "/en/product/features/ai-scheduling",
      ] as RouteTuple,
      scheduleMockupsView1Sv: [
        "/platform/sv/schedule-mockups/view-1-unplanned.html",
        "/produkt/funktioner/ai-schemalaggning",
      ] as RouteTuple,
      scheduleMockupsView1En: [
        "/platform/en/schedule-mockups/view-1-unplanned.html",
        "/en/product/features/ai-scheduling",
      ] as RouteTuple,
      scheduleMockupsView2Sv: [
        "/platform/sv/schedule-mockups/view-2-optimized.html",
        "/produkt/funktioner/ai-schemalaggning",
      ] as RouteTuple,
      scheduleMockupsView2En: [
        "/platform/en/schedule-mockups/view-2-optimized.html",
        "/en/product/features/ai-scheduling",
      ] as RouteTuple,
      scheduleMockupsView3Sv: [
        "/platform/sv/schedule-mockups/view-3-recurring.html",
        "/produkt/funktioner/ai-schemalaggning",
      ] as RouteTuple,
      scheduleMockupsView3En: [
        "/platform/en/schedule-mockups/view-3-recurring.html",
        "/en/product/features/ai-scheduling",
      ] as RouteTuple,
      scheduleMockupsView4Sv: [
        "/platform/sv/schedule-mockups/view-4-comparison.html",
        "/produkt/funktioner/ai-schemalaggning",
      ] as RouteTuple,
      scheduleMockupsView4En: [
        "/platform/en/schedule-mockups/view-4-comparison.html",
        "/en/product/features/ai-scheduling",
      ] as RouteTuple,
    },
    platformCaireCorePrototypeDeepLinks: {
      essFsrIndexSv: [
        "/platform/sv/ess-fsr/index.html",
        "/produkt/funktioner/ai-schemalaggning",
      ] as RouteTuple,
      essFsrIndexEn: [
        "/platform/en/ess-fsr/index.html",
        "/en/product/features/ai-scheduling",
      ] as RouteTuple,
      essFsrMockupsIndexEn: [
        "/platform/en/ess-fsr/mockups/index.html",
        "/en/product/features/ai-scheduling",
      ] as RouteTuple,
      essFsrUnifiedDashboardEn: [
        "/platform/en/ess-fsr/mockups/unified-dashboard.html",
        "/en/product/features/ai-scheduling",
      ] as RouteTuple,
    },
  } as RedirectValue,
};

// For backward compatibility, define resources as an alias to resourcesCategory.index
Object.defineProperty(routes, "resources", {
  get: function () {
    return this.resourcesCategory.index;
  },
});

// Lookup helpers live in routeHelpers.ts so this file stays route data only
// (scripts/quality/cast-oversize-ratchet: routes.ts may not grow).
export { findRouteKey, getLocalizedPath, getRouteTuple } from "./routeHelpers";
